import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Film, ImagePlus, Loader2, Mic, Paperclip, Send, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { jobStatusLabel, toMediaUrl, waitForJobResultUrl } from '../../lib/jobsApi';
import GalleryMediaPicker, { GalleryPickResult } from '../GalleryMediaPicker';
import SelectMenu from '../ui/SelectMenu';
import EditorVideoModelModal from './EditorVideoModelModal';
import EditorImageModelModal from './EditorImageModelModal';
import EditorImagePurposeModal, { type EditorImagePurpose } from './EditorImagePurposeModal';
import EditorVoiceModal, { EditorVoiceValue } from './EditorVoiceModal';
import ModalOverlay from '../ui/ModalOverlay';
import { uploadViaCdn } from '../../lib/uploadMedia';
import { isCdnUploadEnabled } from '../../lib/mediaUrl';
import { creditChip, formatChatCredits } from '../../lib/chatCredits';
import type { EditorSegment } from '../../types/editor';
import type { VideoModelPickerValue } from '../video/VideoModelPicker';
import {
  inferVideoRequestComplexity,
  looksLikeAvatarVideoRequest,
  looksLikeSpokenDialogue,
  rankVideoModelsForRequest,
} from '../../lib/videoModelCatalog';
import type {
  EditorAgentHistoryItem,
  EditorAgentPlan,
  EditorAgentResponse,
  EditorAskField,
  EditorTimelineContext,
} from '../../lib/editorAgent';
import {
  dataUrlToFile,
  fetchOrgMediaAssets,
  galleryListStatus,
  galleryThumbSrc,
  imagePurposeFromAsset,
  setMediaImagePurpose,
  type MediaAsset,
} from '../../lib/mediaGallery';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  listEditorChatThreads,
  upsertEditorChatThread,
  type EditorChatHistoryMsg,
  type EditorChatThread,
  type EditorChatTimelineAudio,
  type EditorChatTimelineCaption,
  type EditorChatTimelineClip,
} from '../../lib/editorChatHistory';

type ChatImage = {
  id: string;
  url: string;
  preview: string;
  label: string;
  kind?: 'image' | 'video';
  role?: 'image' | 'clip';
  segmentId?: string;
  generationContext?: string;
  purpose?: EditorImagePurpose;
  assetId?: string;
  file?: File;
};

type ChatAudio = {
  url: string;
  name: string;
  duration: number;
};

type ChatMsg = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  status?: string;
  images?: ChatImage[];
  audio?: ChatAudio | null;
  modelLabel?: string;
  credits?: number;
  inputTokens?: number;
  outputTokens?: number;
  generationCredits?: number;
  generationKind?: 'video' | 'image' | 'audio';
  ask?: { fields: EditorAskField[] };
  askAnswers?: Record<string, string>;
  askResolved?: boolean;
  plan?: EditorAgentPlan;
  planConfirmed?: boolean;
};

export type InsertGeneratedArgs = {
  url: string;
  label: string;
  kind: 'video' | 'image';
  atSeconds?: number;
  index?: number;
  jobId?: string;
  mediaAssetId?: string;
  generationContext?: string;
};

type Props = {
  selectedSegment: EditorSegment | null;
  canvasAspect: '16:9' | '9:16' | '1:1' | '4:3' | '3:4';
  getUserOrgId: () => Promise<string | null>;
  prepareClipBlob: (seg: EditorSegment) => Promise<Blob>;
  uploadBaseVideo: (orgId: string, blob: Blob) => Promise<string>;
  getAudioDuration: (blob: Blob) => Promise<number>;
  onVideoGenerated: (videoUrl: string, label: string) => void | Promise<void>;
  onInsertGenerated?: (args: InsertGeneratedArgs) => void | Promise<void>;
  onSplitAt?: (atSeconds: number) => void | Promise<void>;
  onAddCaption?: (caption: { text: string; startSeconds: number; endSeconds: number; position?: 'top' | 'center' | 'bottom'; isImpact?: boolean }) => void;
  onGenerateCaptions?: () => void | Promise<void>;
  onAddAudioTrack?: (args: { url: string; name?: string; startOffset?: number; kind?: 'voice' | 'sfx' | 'music' | 'import'; loop?: boolean; generationContext?: string }) => void | Promise<number | void>;
  onSetClipContext?: (clipId: string, context: string) => void;
  onUpdateTimelineAudio?: (mix: {
    voice_volume?: number;
    clip_volume?: number;
    music_volume?: number;
    sfx_volume?: number;
    music_loop?: boolean;
    music_speed?: number;
    mute_music?: boolean;
    mute_sfx?: boolean;
    mute_voice?: boolean;
    mute_clips?: boolean;
  }) => void;
  onRestoreTimelineClips?: (
    clips: EditorChatTimelineClip[],
    audio?: EditorChatTimelineAudio[],
    captions?: EditorChatTimelineCaption[],
  ) => void | Promise<void>;
  timelineContext?: EditorTimelineContext;
  variant?: 'dock' | 'panel';
  getSegment?: (id: string) => EditorSegment | null;
  onHistoryChange?: (entries: EditorChatThread[], activeId: string | null) => void;
};

export type EditorChatHandle = {
  startNewChat: () => void;
  restoreChat: (thread: EditorChatThread) => void;
  attachFrame: (dataUrl: string, label?: string) => Promise<void>;
};

const VIDEO_ASK_IDS = new Set([
  'video_model_id',
  'resolution_id',
  'resolution',
  'duration_seconds',
  'duration',
  'generate_audio',
]);

const IMAGE_ASK_IDS = new Set(['image_model_id']);
const VOICE_ASK_IDS = new Set(['voice_model_id', 'voice_id']);
const SFX_ASK_IDS = new Set(['sfx_model_id']);
const MUSIC_ASK_IDS = new Set(['music_model_id']);
const MEDIA_ASK_IDS = new Set(['need_person_image', 'need_reference_image', 'need_avatar_audio']);

function planBlob(
  plan?: { scenes?: Array<{ label?: string; prompt?: string; notes?: string }> } | null,
  extra = '',
) {
  return [
    extra,
    ...(plan?.scenes || []).map((s) => `${s.label || ''} ${s.prompt || ''} ${s.notes || ''}`),
  ].join('\n');
}

function planLooksLikeTalking(
  plan?: { scenes?: Array<{ label?: string; prompt?: string; notes?: string }> } | null,
  extra = '',
) {
  const blob = planBlob(plan, extra);
  return looksLikeSpokenDialogue(blob) || looksLikeAvatarVideoRequest(blob);
}

function planLooksLikeAvatar(
  plan?: { scenes?: Array<{ label?: string; prompt?: string; notes?: string }> } | null,
  extra = '',
) {
  return looksLikeAvatarVideoRequest(planBlob(plan, extra));
}

function wantsSceneBackdrop(text?: string): boolean {
  return /<<<image_\d+>>>|ao fundo|de fundo|background|em frente (à|a|da|ao|do)|frente (à|a) (obra|fachada|pr[eé]dio)|use (essa|esta|a) imagem|imagem gerada|com (a |o )?(obra|fachada|pr[eé]dio)|street.?level|vista frontal|fachada da obra|obra conclu[ií]da/i.test(text || '');
}

function isVoiceUiBoilerplate(text?: string): boolean {
  return /escolha (a )?voz|modelo de locu[cç][aã]o|pre[cç]o aparece no modal|seguir com a voz|escolha o modelo de (som|m[uú]sica)/i.test(text || '');
}

function extractSpokenLine(text?: string): string | undefined {
  const patterns = [
    /[“«"]([^”»"\n]{6,500})[”»"]/,
    /[‘']([^’'\n]{6,500})[’']/,
  ];
  for (const re of patterns) {
    const m = String(text || '').match(re);
    if (m?.[1]) {
      const line = m[1].replace(/\s+/g, ' ').trim();
      if (line.length >= 6 && !isVoiceUiBoilerplate(line)) return line;
    }
  }
  return undefined;
}

function voiceModalSeedText(msg?: ChatMsg | null): string {
  if (!msg) return '';
  const fromAnswers = msg.askAnswers?.voice_text?.trim();
  if (fromAnswers && !isVoiceUiBoilerplate(fromAnswers)) return fromAnswers;
  const fromPlan = extractSpokenLine(planBlob(msg.plan));
  if (fromPlan) return fromPlan;
  const fromText = extractSpokenLine(msg.text);
  if (fromText) return fromText;
  if (msg.text && !isVoiceUiBoilerplate(msg.text) && !/close-?up|gesticul|<<<image|cinematic/i.test(msg.text)) {
    return msg.text.trim();
  }
  return '';
}

function parsePlanJson(raw?: string) {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as { title?: string; scenes?: Array<{ label?: string; prompt?: string; duration_seconds?: number; notes?: string }> };
    if (!obj?.scenes?.length) return null;
    return normalizeAgentPlan({ title: obj.title, scenes: obj.scenes });
  } catch {
    return null;
  }
}

function normalizeAgentPlan(plan: {
  title?: string;
  scenes: Array<{ label?: string; prompt?: string; duration_seconds?: number; notes?: string }>;
}): EditorAgentPlan {
  return {
    title: plan.title || 'Plano',
    scenes: plan.scenes.map((s) => ({
      label: String(s.label || 'Cena').slice(0, 80),
      prompt: String(s.prompt || '').trim(),
      duration_seconds: (() => {
        const n = typeof s.duration_seconds === 'number' ? s.duration_seconds : Number(s.duration_seconds);
        if (!Number.isFinite(n) || n <= 0) return 8;
        return Math.max(4, Math.min(15, Math.round(n)));
      })(),
      notes: typeof s.notes === 'string' ? s.notes : undefined,
    })).filter((s) => s.prompt),
  };
}

function isMusicAskField(field: { id: string; label?: string }): boolean {
  if (MUSIC_ASK_IDS.has(field.id)) return true;
  return /music_model|m[uú]sica|trilha|instrumental|beat|soundtrack/i.test(`${field.id} ${field.label || ''}`)
    && !/sfx_model|voz|voice/i.test(`${field.id} ${field.label || ''}`);
}

function isSfxAskField(field: { id: string; label?: string }): boolean {
  if (isMusicAskField(field)) return false;
  if (SFX_ASK_IDS.has(field.id)) return true;
  return /sfx_model|sfx|ambiente|efeito|foley|sound/i.test(`${field.id} ${field.label || ''}`);
}

function isVoiceAskField(field: { id: string; label?: string }): boolean {
  if (isMusicAskField(field) || isSfxAskField(field)) return false;
  if (VOICE_ASK_IDS.has(field.id)) return true;
  return /voice|voz|locu|tts|narra/i.test(`${field.id} ${field.label || ''}`);
}

function looksLikeMusicIntent(text?: string): boolean {
  return /m[uú]sica|trilha(\s+sonora)?|\bbeats?\b|instrumental|soundtrack|backing\s*track|minimax|add[\s-]?instrumental|generate[\s-]?music/i.test(text || '');
}

function isMediaAskField(field: { id: string }): boolean {
  return MEDIA_ASK_IDS.has(field.id);
}

function needsImagePurpose(img: ChatImage): boolean {
  return (img.kind || 'image') === 'image' && img.role !== 'clip' && !img.purpose;
}

function videoModelHint(text?: string, topLabel?: string): string {
  const complexity = inferVideoRequestComplexity(text || '');
  const why = complexity === 'complex'
    ? 'pedido mais elaborado — modelos com mais crédito no topo'
    : complexity === 'simple'
      ? 'pedido mais simples — modelos mais baratos no topo'
      : 'equilíbrio custo/qualidade no topo';
  const tip = topLabel ? ` Sugestão: ${topLabel} (${why}).` : '';
  if (looksLikeAvatarVideoRequest(text || '')) {
    return `Lip-sync de um rosto real: Kling Avatar (foto marcada como Avatar + áudio 2–60s).${tip}`;
  }
  if (looksLikeSpokenDialogue(text || '')) {
    if (wantsSceneBackdrop(text || '')) {
      return `Locutor com fundo da obra: Seedance I2V (foto da fachada = <<<image_1>>>) + áudio nativo. Sem Kling Avatar nem TTS separado.${tip}`;
    }
    return `Locutor genérico: Seedance com áudio nativo. A frase vai no prompt. Sem Kling Avatar nem TTS separado.${tip}`;
  }
  if (/pessoa|homem|mulher|corretor|casal|rosto/i.test(text || '')) {
    return `Pessoa na cena: Seedance + foto da pessoa. Avatar só se for lip-sync de um retrato real.${tip}`;
  }
  return `Seedance para cena/imóvel. Kling Avatar só para lip-sync de um retrato real.${tip}`;
}

function voiceModalKind(
  fields?: Array<{ id: string; label?: string }>,
  text?: string,
): 'music' | 'sfx' | 'voice' {
  if (fields?.some(isMusicAskField) || (fields?.some(isSfxAskField) && looksLikeMusicIntent(text))) {
    return 'music';
  }
  if (fields?.some(isSfxAskField)) return 'sfx';
  return 'voice';
}

const CHAT_REF_MIME = 'application/x-seu-chat-ref';
const IMAGE_EXT_RE = /\.(jpe?g|jfif|pjpeg|png|webp|gif|bmp|svg|avif|heic|heif|tif|tiff)$/i;
const VIDEO_EXT_RE = /\.(mp4|webm|mov|m4v|mkv)$/i;
const BROWSER_PREVIEW_EXT_RE = /\.(jpe?g|jfif|pjpeg|png|webp|gif|bmp|svg|avif)$/i;
const MIME_BY_IMAGE_EXT: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  pjpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  avif: 'image/avif',
  heic: 'image/heic',
  heif: 'image/heif',
  tif: 'image/tiff',
  tiff: 'image/tiff',
};

function guessLocalMediaKind(file: File): 'image' | 'video' | null {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (IMAGE_EXT_RE.test(file.name)) return 'image';
  if (VIDEO_EXT_RE.test(file.name)) return 'video';
  return null;
}

function fileWithMime(file: File, kind: 'image' | 'video'): File {
  if (file.type && file.type !== 'application/octet-stream') return file;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const fallback = kind === 'video' ? 'video/mp4' : (MIME_BY_IMAGE_EXT[ext] || 'image/jpeg');
  return new File([file], file.name, { type: fallback });
}

function extForMime(mime: string, kind: 'image' | 'video'): string {
  const t = (mime || '').toLowerCase().split(';')[0].trim();
  if (kind === 'video') {
    if (t.includes('webm')) return 'webm';
    if (t.includes('quicktime')) return 'mov';
    return 'mp4';
  }
  if (t.includes('png')) return 'png';
  if (t.includes('webp')) return 'webp';
  if (t.includes('gif')) return 'gif';
  if (t.includes('bmp')) return 'bmp';
  if (t.includes('svg')) return 'svg';
  if (t.includes('avif')) return 'avif';
  return 'jpg';
}

/** Clipboard / paste: image files (screenshots, copied photos). */
function filesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return [];
  const out: File[] = [];
  const seen = new Set<string>();
  const push = (file: File) => {
    const key = `${file.name}:${file.size}:${file.type}:${file.lastModified}`;
    if (seen.has(key)) return;
    seen.add(key);
    const kind = guessLocalMediaKind(file);
    if (!kind) return;
    const named = (!file.name || file.name === 'image.png' || file.name === 'blob')
      ? new File([file], `colado-${Date.now()}-${out.length}.${extForMime(file.type, kind)}`, {
        type: file.type || (kind === 'video' ? 'video/mp4' : 'image/png'),
        lastModified: file.lastModified || Date.now(),
      })
      : file;
    out.push(named);
  };
  if (data.files?.length) {
    Array.from(data.files).forEach(push);
  }
  if (data.items?.length) {
    Array.from(data.items).forEach((item) => {
      if (item.kind !== 'file') return;
      if (!/^image\//i.test(item.type) && !/^video\//i.test(item.type)) return;
      const file = item.getAsFile();
      if (file) push(file);
    });
  }
  return out;
}

function browserCanPreviewImage(type?: string, name?: string): boolean {
  const t = (type || '').toLowerCase().split(';')[0].trim();
  if (t.includes('heic') || t.includes('heif') || t.includes('tiff')) return false;
  if (t.startsWith('image/') && (t.includes('jpeg') || t.includes('jpg') || t.includes('png') || t.includes('gif') || t.includes('webp') || t.includes('bmp') || t.includes('svg') || t.includes('avif'))) {
    return true;
  }
  return BROWSER_PREVIEW_EXT_RE.test(name || '');
}

async function makeBrowserPreviewUrl(file: File): Promise<string> {
  if (browserCanPreviewImage(file.type, file.name)) {
    return URL.createObjectURL(file);
  }
  try {
    const bitmap = await createImageBitmap(file);
    const max = 320;
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas');
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((next) => (next ? resolve(next) : reject(new Error('thumb'))), 'image/jpeg', 0.82);
    });
    return URL.createObjectURL(blob);
  } catch {
    return URL.createObjectURL(file);
  }
}

function serializeComposer(el: HTMLElement | null): string {
  if (!el) return '';
  let out = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent || '';
      return;
    }
    if (node instanceof HTMLElement && node.dataset.refChip === '1') {
      out += `@[${node.dataset.refLabel || 'imagem'}]`;
      return;
    }
    node.childNodes.forEach(walk);
  };
  walk(el);
  return out.replace(/\u00a0/g, ' ');
}

function renderPromptWithRefChips(text: string) {
  const parts = text.split(/(@\[[^\]]+\])/g).filter(Boolean);
  return parts.map((part, index) => {
    const match = part.match(/^@\[([^\]]+)\]$/);
    if (!match) return <span key={index}>{part}</span>;
    return (
      <span key={index} className="chat-ref-chip" contentEditable={false}>
        {match[1]}
      </span>
    );
  });
}

function isSoundJobKind(kind?: string) {
  return kind === 'sfx' || kind === 'music';
}

function audioJobLabel(kind?: string) {
  if (kind === 'music') return 'Música';
  if (kind === 'sfx') return 'Som';
  return 'Voz';
}

function isHttpUrl(url?: string | null): url is string {
  return Boolean(url && /^https?:\/\//i.test(url));
}

async function promoteLocalChatImage(img: ChatImage, orgId: string): Promise<ChatImage> {
  if (isHttpUrl(img.url)) return img;
  const blob = img.file || await (async () => {
    const src = img.url || img.preview;
    if (!src) throw new Error('Arquivo local sem dados.');
    const res = await fetch(src);
    if (!res.ok) throw new Error('Falha ao ler o arquivo local.');
    const type = img.file?.type || res.headers.get('content-type') || (img.kind === 'video' ? 'video/mp4' : 'image/jpeg');
    return new Blob([await res.arrayBuffer()], { type });
  })();
  const uploaded = await uploadViaCdn({
    organizationId: orgId,
    file: blob,
    contentType: blob.type || (img.kind === 'video' ? 'video/mp4' : 'image/jpeg'),
    filename: img.file?.name || `${img.label || 'ref'}.${img.kind === 'video' ? 'mp4' : 'jpg'}`,
    purpose: 'frame',
  });
  return { ...img, url: uploaded.publicUrl, file: undefined };
}

function measureAudioDuration(url: string): Promise<number> {
  return new Promise((resolve) => {
    const el = new Audio();
    el.preload = 'metadata';
    const done = (value: number) => {
      el.removeAttribute('src');
      el.load();
      resolve(value);
    };
    el.onloadedmetadata = () => {
      const d = el.duration;
      done(Number.isFinite(d) && d > 0 && d !== Infinity ? d : 0);
    };
    el.onerror = () => done(0);
    window.setTimeout(() => done(0), 8000);
    el.src = url;
  });
}

function toPersistedMessages(msgs: ChatMsg[]): EditorChatHistoryMsg[] {
  return msgs
    .filter((m) => m.text || (m.images && m.images.length))
    .map((m) => ({
      id: m.id,
      role: m.role,
      text: m.text,
      modelLabel: m.modelLabel,
      credits: m.credits,
      generationCredits: m.generationCredits,
      generationKind: m.generationKind,
      plan: m.plan,
      planConfirmed: m.planConfirmed,
      audio: m.audio && isHttpUrl(m.audio.url) ? m.audio : null,
      images: (m.images || [])
        .map((img) => ({
          id: img.id,
          url: isHttpUrl(img.url) ? img.url : '',
          preview: isHttpUrl(img.preview) ? img.preview : (isHttpUrl(img.url) ? img.url : ''),
          label: img.label,
          kind: img.kind,
          role: img.role,
          segmentId: img.segmentId,
          generationContext: img.generationContext,
          purpose: img.purpose,
          assetId: img.assetId && !img.assetId.startsWith('local-') ? img.assetId : undefined,
        }))
        .filter((img) => img.url || img.preview),
    }));
}

function toPersistedTimeline(clips: EditorTimelineContext['clips'] | undefined): EditorChatTimelineClip[] {
  return (clips || [])
    .filter((clip) => isHttpUrl(clip.sourceUrl))
    .map((clip) => ({
      id: clip.id,
      label: clip.label,
      type: clip.type,
      sourceUrl: clip.sourceUrl as string,
      thumbnailUrl: isHttpUrl(clip.thumbnailUrl) ? clip.thumbnailUrl : undefined,
      mediaAssetId: clip.mediaAssetId,
      generationContext: clip.generationContext,
      start: clip.start,
      end: clip.end,
      duration: clip.duration,
      originalDuration: clip.originalDuration,
      speed: clip.speed,
      trimStart: clip.trimStart,
      trimEnd: clip.trimEnd,
      volume: clip.volume,
      muted: Boolean(clip.muted),
      fit: clip.fit,
      transitionOut: clip.transitionOut,
      brightnessCurve: clip.brightnessCurve,
      brightnessCurveSeconds: clip.brightnessCurveSeconds,
      motion: clip.motion,
    }));
}

function toPersistedAudio(tracks: EditorTimelineContext['audioTracks'] | undefined): EditorChatTimelineAudio[] {
  return (tracks || [])
    .filter((track) => isHttpUrl(track.sourceUrl) && Number.isFinite(track.duration) && track.duration > 0)
    .map((track) => ({
      id: track.id,
      name: track.name,
      sourceUrl: track.sourceUrl,
      duration: track.duration,
      startOffset: track.startOffset,
      volume: track.volume,
      muted: Boolean(track.muted),
      loop: Boolean(track.loop),
      speed: track.speed,
      kind: track.kind,
      trimStart: track.trimStart || 0,
      trimEnd: track.trimEnd || 0,
      generationContext: track.generationContext,
    }));
}

function toPersistedCaptions(captions: EditorTimelineContext['captions'] | undefined): EditorChatTimelineCaption[] {
  return (captions || []).map((cap) => ({
    id: cap.id,
    text: cap.text,
    startTime: cap.startTime,
    endTime: cap.endTime,
    isImpactPhrase: Boolean(cap.isImpactPhrase),
    position: cap.position,
  }));
}

function clipKindFromMessage(img: { kind?: string; role?: string; url?: string }): 'image' | 'video' {
  if (img.kind === 'video' || img.role === 'clip') return 'video';
  if (/\.(mp4|webm|mov)(\?|#|$)/i.test(img.url || '')) return 'video';
  return 'image';
}

function clipsFromMessages(messages: Array<{ images?: EditorChatHistoryMsg['images'] }>): EditorChatTimelineClip[] {
  const clips: EditorChatTimelineClip[] = [];
  for (const msg of messages) {
    for (const img of msg.images || []) {
      const url = isHttpUrl(img.url) ? img.url : (isHttpUrl(img.preview) ? img.preview : '');
      if (!url) continue;
      if (clips.some((c) => c.sourceUrl === url)) continue;
      clips.push({
        id: img.segmentId || img.id,
        label: img.label,
        type: clipKindFromMessage(img),
        sourceUrl: url,
        thumbnailUrl: isHttpUrl(img.preview) ? img.preview : undefined,
      });
    }
  }
  return clips;
}

function timelineClipsFromThread(thread: EditorChatThread): EditorChatTimelineClip[] {
  if (thread.timelineClips) return thread.timelineClips;
  return clipsFromMessages(thread.messages);
}

function timelineAudioFromThread(thread: EditorChatThread): EditorChatTimelineAudio[] | undefined {
  if (thread.timelineAudio !== undefined) return thread.timelineAudio;
  const fromMsgs: EditorChatTimelineAudio[] = [];
  for (const msg of thread.messages) {
    if (!msg.audio || !isHttpUrl(msg.audio.url)) continue;
    if (fromMsgs.some((t) => t.sourceUrl === msg.audio!.url)) continue;
    fromMsgs.push({
      id: `${msg.id}-audio`,
      name: msg.audio.name || (msg.modelLabel === 'Música' ? 'Música' : msg.modelLabel === 'Som' ? 'Som' : 'Voz'),
      sourceUrl: msg.audio.url,
      duration: msg.audio.duration || 8,
      startOffset: 0,
      volume: 1,
      kind: msg.modelLabel === 'Música' ? 'music' : msg.modelLabel === 'Som' ? 'sfx' : 'voice',
      generationContext: msg.audio.name || msg.text,
    });
  }
  return fromMsgs.length ? fromMsgs : undefined;
}

function ComposerImageThumb({
  img,
  onRemove,
  onPurpose,
}: {
  img: ChatImage;
  onRemove: () => void;
  onPurpose: (purpose: EditorImagePurpose) => void;
}) {
  const src = img.preview || img.url;
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [src]);

  return (
    <div style={{ position: 'relative', width: 72 }}>
      <div
        draggable
        title="Arraste para o texto para citar esta imagem"
        onDragStart={(e) => {
          e.dataTransfer.setData(CHAT_REF_MIME, JSON.stringify({ id: img.id, label: img.label }));
          e.dataTransfer.setData('text/plain', `@[${img.label}]`);
          e.dataTransfer.effectAllowed = 'copy';
        }}
        style={{
        width: 72,
        height: 56,
        borderRadius: 10,
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.06)',
        position: 'relative',
        cursor: 'grab',
      }}>
        {(!loaded || failed) && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            color: 'rgba(255,255,255,0.55)',
          }}>
            {failed ? <ImagePlus size={16} /> : <Loader2 size={16} className="spin" />}
          </div>
        )}
        {src && img.kind === 'video' ? (
          <video
            src={src}
            muted
            preload="metadata"
            draggable={false}
            onLoadedData={() => setLoaded(true)}
            onError={() => setFailed(true)}
            style={{
              width: 72,
              height: 56,
              objectFit: 'cover',
              display: 'block',
              opacity: loaded && !failed ? 1 : 0,
              pointerEvents: 'none',
            }}
          />
        ) : src && !failed ? (
          <img
            src={src}
            alt={img.label}
            draggable={false}
            ref={(el) => {
              if (el && el.complete && el.naturalWidth > 0) setLoaded(true);
            }}
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
            style={{
              width: 72,
              height: 56,
              objectFit: 'cover',
              display: 'block',
              opacity: loaded ? 1 : 0,
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </div>
      <button
        type="button"
        onClick={onRemove}
        style={{
          position: 'absolute', top: -6, right: -6, width: 16, height: 16,
          border: 'none', borderRadius: '50%', background: '#111827',
          color: '#fff', cursor: 'pointer', padding: 0, display: 'grid', placeItems: 'center',
        }}
      >
        <X size={9} />
      </button>
      {img.kind !== 'video' && (
        <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
          {([
            { id: 'avatar' as const, label: 'Avatar' },
            { id: 'photo' as const, label: 'Foto' },
          ]).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onPurpose(opt.id)}
              style={{
                flex: 1,
                border: 'none',
                borderRadius: 5,
                padding: '2px 0',
                fontSize: '0.55rem',
                fontFamily: 'inherit',
                cursor: 'pointer',
                background: img.purpose === opt.id
                  ? (opt.id === 'avatar' ? 'rgba(244,114,182,0.85)' : 'rgba(99,102,241,0.85)')
                  : 'rgba(255,255,255,0.08)',
                color: img.purpose === opt.id ? '#fff' : 'rgba(255,255,255,0.55)',
                fontWeight: img.purpose === opt.id ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RefChip({
  label,
  kind,
  onRemove,
}: {
  label: string;
  kind?: 'image' | 'video';
  onRemove?: () => void;
}) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      maxWidth: 180,
      padding: '2px 6px 2px 7px',
      borderRadius: 999,
      background: 'rgba(99,102,241,0.2)',
      border: '1px solid rgba(129,140,248,0.32)',
      fontSize: '0.66rem',
      fontWeight: 600,
      color: '#e0e7ff',
      lineHeight: 1.2,
      verticalAlign: 'middle',
    }}>
      {kind === 'video' ? <Film size={11} /> : <ImagePlus size={11} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          style={{
            border: 'none', background: 'none', color: 'rgba(224,231,255,0.7)',
            cursor: 'pointer', padding: 0, display: 'grid', placeItems: 'center',
          }}
        >
          <X size={10} />
        </button>
      )}
    </span>
  );
}

const chip = creditChip;

function MessageBubble({
  msg,
  onRemoveImage,
  onAskChange,
  onAskConfirm,
  onPlanConfirm,
  onOpenMedia,
  onAttachGallery,
  onAttachAudio,
}: {
  msg: ChatMsg;
  onRemoveImage?: (id: string) => void;
  onAskChange?: (fieldId: string, value: string) => void;
  onAskConfirm?: () => void;
  onPlanConfirm?: () => void;
  onOpenMedia?: (img: ChatImage) => void;
  onAttachGallery?: () => void;
  onAttachAudio?: () => void;
}) {
  const mine = msg.role === 'user';
  const imageRefs = (msg.images || []).filter((img) => img.role !== 'clip');
  const clipRefs = (msg.images || []).filter((img) => img.role === 'clip');
  return (
    <div style={{
      alignSelf: mine ? 'flex-end' : 'flex-start',
      maxWidth: mine ? 360 : 420,
      padding: imageRefs.length ? '8px 8px 8px' : '8px 11px',
      borderRadius: mine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
      background: mine ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
      border: `1px solid ${mine ? 'rgba(129,140,248,0.28)' : 'rgba(255,255,255,0.07)'}`,
      fontSize: '0.72rem',
      lineHeight: 1.45,
      color: 'rgba(255,255,255,0.88)',
    }}>
      {imageRefs.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: msg.text || clipRefs.length ? 8 : 0 }}>
          {imageRefs.map((img) => (
            <div key={img.id} style={{ position: 'relative' }}>
              {img.kind === 'video' ? (
                <video
                  src={img.preview || img.url}
                  muted
                  preload="metadata"
                  onClick={() => onOpenMedia?.(img)}
                  style={{
                    width: 72, height: 72, objectFit: 'cover',
                    borderRadius: 10, display: 'block',
                    border: '1px solid rgba(255,255,255,0.08)',
                    cursor: onOpenMedia ? 'pointer' : 'default',
                  }}
                />
              ) : (
                <>
                  <img
                    src={img.preview || img.url}
                    alt={img.label}
                    onClick={() => onOpenMedia?.(img)}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
                      if (fallback) fallback.style.display = 'grid';
                    }}
                    style={{
                      width: 72, height: 72, objectFit: 'cover',
                      borderRadius: 10, display: 'block',
                      border: '1px solid rgba(255,255,255,0.08)',
                      cursor: onOpenMedia ? 'pointer' : 'default',
                    }}
                  />
                  <div style={{
                    display: 'none',
                    width: 72, height: 72, placeItems: 'center',
                    borderRadius: 10, background: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.5)',
                  }}>
                    <ImagePlus size={18} />
                  </div>
                </>
              )}
              {onRemoveImage && (
                <button
                  type="button"
                  onClick={() => onRemoveImage(img.id)}
                  style={{
                    position: 'absolute', top: -5, right: -5, width: 16, height: 16,
                    border: 'none', borderRadius: '50%', background: '#111827',
                    color: '#fff', cursor: 'pointer', padding: 0, display: 'grid', placeItems: 'center',
                  }}
                >
                  <X size={9} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {msg.audio && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6,
          fontSize: '0.6rem', color: 'rgba(255,255,255,0.65)',
        }}>
          <Mic size={10} /> {msg.audio.duration > 0 ? `${msg.audio.duration.toFixed(1)}s` : 'áudio'}
        </div>
      )}
      {(clipRefs.length > 0 || msg.text) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          {clipRefs.map((img) => (
            <RefChip key={img.id} label={img.label} kind={img.kind} />
          ))}
          {msg.text ? <span>{renderPromptWithRefChips(msg.text)}</span> : null}
        </div>
      )}
      {msg.status && (
        <div style={{ marginTop: 4, fontSize: '0.62rem', color: '#a5b4fc' }}>{msg.status}</div>
      )}
      {(msg.modelLabel || msg.credits != null || msg.generationCredits != null) && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {msg.modelLabel && <span style={chip}>{msg.modelLabel}</span>}
          {msg.generationCredits != null && msg.generationCredits > 0 && (
            <span style={{ ...chip, background: 'rgba(99,102,241,0.22)', borderColor: 'rgba(129,140,248,0.35)' }}>
              {msg.generationCredits} créditos ({msg.generationKind === 'image' ? 'imagem' : msg.generationKind === 'audio' ? 'voz' : 'vídeo'})
            </span>
          )}
          {msg.credits != null && (
            <span style={chip} title={msg.inputTokens || msg.outputTokens
              ? `${msg.inputTokens || 0} inp · ${msg.outputTokens || 0} out`
              : undefined}
            >
              {formatChatCredits(msg.credits)} créditos (chat
              {msg.inputTokens || msg.outputTokens
                ? ` · ${msg.inputTokens || 0} inp / ${msg.outputTokens || 0} out`
                : ''}
              )
            </span>
          )}
        </div>
      )}
      {msg.ask && msg.ask.fields.filter((f) => !VIDEO_ASK_IDS.has(f.id) && !IMAGE_ASK_IDS.has(f.id) && !isVoiceAskField(f) && !isSfxAskField(f) && !isMusicAskField(f) && !isMediaAskField(f)).length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {msg.ask.fields.filter((f) => !VIDEO_ASK_IDS.has(f.id) && !IMAGE_ASK_IDS.has(f.id) && !isVoiceAskField(f) && !isSfxAskField(f) && !isMusicAskField(f) && !isMediaAskField(f)).map((field) => (
            <div key={field.id}>
              <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>{field.label}</div>
              <SelectMenu
                compact
                aria-label={field.label}
                value={msg.askAnswers?.[field.id] || field.options[0]?.value || ''}
                options={field.options}
                onChange={(v) => onAskChange?.(field.id, v)}
                disabled={msg.askResolved}
              />
            </div>
          ))}
          {!msg.askResolved && onAskConfirm && !msg.ask.fields.some((f) => f.id === 'video_model_id' || f.id === 'image_model_id' || isVoiceAskField(f) || isSfxAskField(f) || isMusicAskField(f) || isMediaAskField(f)) && (
            <button
              type="button"
              onClick={onAskConfirm}
              style={{
                alignSelf: 'flex-start', border: 'none', borderRadius: 8,
                background: '#6366f1', color: '#fff', fontSize: '0.68rem',
                padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
              }}
            >
              Continuar
            </button>
          )}
        </div>
      )}
      {msg.plan && msg.plan.scenes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {msg.plan.scenes.map((scene, idx) => (
            <div
              key={`${scene.label}-${idx}`}
              style={{
                padding: '8px 9px',
                borderRadius: 10,
                background: 'rgba(0,0,0,0.22)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#c7d2fe', marginBottom: 3 }}>
                {idx + 1}. {scene.label}
                {` · ${scene.duration_seconds && scene.duration_seconds > 0 ? scene.duration_seconds : 8}s`}
              </div>
              <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.78)' }}>{scene.prompt}</div>
              {scene.notes ? (
                <div style={{ marginTop: 4, fontSize: '0.58rem', color: 'rgba(255,255,255,0.45)' }}>{scene.notes}</div>
              ) : null}
            </div>
          ))}
          <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>
            Total: {msg.plan.scenes.reduce((sum, s) => sum + (s.duration_seconds && s.duration_seconds > 0 ? s.duration_seconds : 8), 0)}s
          </div>
          {!msg.planConfirmed && onPlanConfirm && (
            <button
              type="button"
              onClick={onPlanConfirm}
              style={{
                alignSelf: 'flex-start', border: 'none', borderRadius: 8,
                background: '#6366f1', color: '#fff', fontSize: '0.68rem',
                padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
              }}
            >
              Confirmar plano
            </button>
          )}
        </div>
      )}
      {msg.ask && !msg.askResolved && msg.ask.fields.some((f) => f.id === 'image_model_id') && onAskConfirm && (
        <button
          type="button"
          onClick={onAskConfirm}
          style={{
            marginTop: 10, alignSelf: 'flex-start', border: 'none', borderRadius: 8,
            background: '#6366f1', color: '#fff', fontSize: '0.68rem',
            padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
          }}
        >
          Escolher modelo de imagem
        </button>
      )}
      {((msg.ask && !msg.askResolved && msg.ask.fields.some((f) => f.id === 'video_model_id'))) && onAskConfirm && (
        <button
          type="button"
          onClick={onAskConfirm}
          style={{
            marginTop: 10, alignSelf: 'flex-start', border: 'none', borderRadius: 8,
            background: '#6366f1', color: '#fff', fontSize: '0.68rem',
            padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
          }}
        >
          Escolher modelo de vídeo
        </button>
      )}
      {msg.ask && !msg.askResolved && voiceModalKind(msg.ask.fields, msg.text) === 'music' && onAskConfirm && (
        <button
          type="button"
          onClick={onAskConfirm}
          style={{
            marginTop: 10, alignSelf: 'flex-start', border: 'none', borderRadius: 8,
            background: '#6366f1', color: '#fff', fontSize: '0.68rem',
            padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
          }}
        >
          Escolher modelo de música
        </button>
      )}
      {msg.ask && !msg.askResolved && voiceModalKind(msg.ask.fields, msg.text) === 'sfx' && onAskConfirm && (
        <button
          type="button"
          onClick={onAskConfirm}
          style={{
            marginTop: 10, alignSelf: 'flex-start', border: 'none', borderRadius: 8,
            background: '#6366f1', color: '#fff', fontSize: '0.68rem',
            padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
          }}
        >
          Escolher modelo de som
        </button>
      )}
      {msg.ask && !msg.askResolved && msg.ask.fields.some(isVoiceAskField) && onAskConfirm && (
        <button
          type="button"
          onClick={onAskConfirm}
          style={{
            marginTop: 10, alignSelf: 'flex-start', border: 'none', borderRadius: 8,
            background: '#6366f1', color: '#fff', fontSize: '0.68rem',
            padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
          }}
        >
          Escolher modelo de voz
        </button>
      )}
      {msg.ask && !msg.askResolved && msg.ask.fields.some((f) => f.id === 'need_person_image' || f.id === 'need_reference_image') && onAttachGallery && (
        <button
          type="button"
          onClick={onAttachGallery}
          style={{
            marginTop: 10, alignSelf: 'flex-start', border: 'none', borderRadius: 8,
            background: '#6366f1', color: '#fff', fontSize: '0.68rem',
            padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
          }}
        >
          {msg.ask.fields.some((f) => f.id === 'need_person_image') ? 'Anexar foto da pessoa' : 'Anexar imagem de referência'}
        </button>
      )}
      {msg.ask && !msg.askResolved && msg.ask.fields.some((f) => f.id === 'need_avatar_audio') && onAttachAudio && (
        <button
          type="button"
          onClick={onAttachAudio}
          style={{
            marginTop: 10, alignSelf: 'flex-start', border: 'none', borderRadius: 8,
            background: '#6366f1', color: '#fff', fontSize: '0.68rem',
            padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
          }}
        >
          Anexar áudio da fala
        </button>
      )}
    </div>
  );
}

const EditorChat = React.forwardRef<EditorChatHandle, Props>(function EditorChat({
  selectedSegment,
  canvasAspect,
  getUserOrgId,
  prepareClipBlob,
  uploadBaseVideo,
  getAudioDuration,
  onVideoGenerated,
  onInsertGenerated,
  onSplitAt,
  onAddCaption,
  onGenerateCaptions,
  onAddAudioTrack,
  onUpdateTimelineAudio,
  onSetClipContext,
  onRestoreTimelineClips,
  timelineContext,
  variant = 'panel',
  getSegment,
  onHistoryChange,
}, ref) {
  const { role } = useOrg();
  const { user } = useAuth();
  const dock = variant === 'dock';
  const [prompt, setPrompt] = useState('');
  const [images, setImages] = useState<ChatImage[]>([]);
  const [purposeQueue, setPurposeQueue] = useState<ChatImage[]>([]);
  const pendingSendAfterPurpose = useRef(false);
  const [audio, setAudio] = useState<ChatAudio | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [historyEntries, setHistoryEntries] = useState<EditorChatThread[]>([]);
  const [historyActiveId, setHistoryActiveId] = useState<string | null>(null);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const openedImageAskId = useRef<string | null>(null);
  const openedVoiceAskId = useRef<string | null>(null);
  const [dropOver, setDropOver] = useState(false);
  const [mediaPreview, setMediaPreview] = useState<ChatImage | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLDivElement>(null);
  const lastRefUrls = useRef<string[]>([]);
  const threadImages = useRef<ChatImage[]>([]);
  const threadAudio = useRef<ChatAudio | null>(null);
  const persistTimer = useRef<number | null>(null);
  const historyActiveIdRef = useRef<string | null>(null);
  const restoreClipsRef = useRef(onRestoreTimelineClips);
  restoreClipsRef.current = onRestoreTimelineClips;
  const hadTimelineClips = useRef(false);
  const timelinePersistReady = useRef(false);
  const restoringRef = useRef(false);
  historyActiveIdRef.current = historyActiveId;

  const selectedImage = selectedSegment?.type === 'image' ? selectedSegment : null;
  const pendingVideoAsk = messages.find((m) => (
    Boolean(!m.askResolved && m.ask?.fields.some((f) => f.id === 'video_model_id'))
  ));
  const pendingImageAsk = messages.find((m) => (
    Boolean(m.ask && !m.askResolved && m.ask.fields.some((f) => f.id === 'image_model_id'))
  ));
  const pendingVoiceAsk = messages.find((m) => (
    Boolean(m.ask && !m.askResolved && m.ask.fields.some((f) => isVoiceAskField(f) || isSfxAskField(f) || isMusicAskField(f)))
  ));
  const lastGeneratedImageUrl = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role !== 'assistant') continue;
      // Only AI-generated stills — never the first photo the user uploaded in chat.
      if (m.generationKind !== 'image') continue;
      const hit = (m.images || []).find((img) => img.kind !== 'video' && isHttpUrl(img.url));
      if (hit) return hit.url;
    }
    return '';
  })();
  const maxRefs = 30;

  const rememberUrls = (urls: string[]) => {
    const next = lastRefUrls.current.slice();
    urls.filter(isHttpUrl).forEach((url) => {
      if (next.indexOf(url) === -1) next.unshift(url);
    });
    lastRefUrls.current = next.slice(0, 30);
  };

  const collectRefUrls = (sentImages: ChatImage[], opts?: { includeHistory?: boolean }) => {
    const urls: string[] = [];
    const add = (url?: string | null) => {
      if (isHttpUrl(url) && !urls.includes(url)) urls.push(url);
    };
    sentImages.forEach((img) => {
      if (img.kind !== 'video') add(img.url);
    });
    // History refs only on plan/model continuations — not on every new typed message.
    if (opts?.includeHistory) {
      lastRefUrls.current.forEach(add);
    }
    add(selectedImage?.sourceUrl);
    return urls;
  };

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending, images.length, audio]);

  const syncComposerPrompt = () => {
    setPrompt(serializeComposer(textareaRef.current));
  };

  const clearComposer = () => {
    if (textareaRef.current) textareaRef.current.innerHTML = '';
    setPrompt('');
  };

  const insertRefChip = (label: string, id?: string) => {
    const root = textareaRef.current;
    if (!root) return;
    const chip = document.createElement('span');
    chip.contentEditable = 'false';
    chip.dataset.refChip = '1';
    chip.dataset.refLabel = label;
    if (id) chip.dataset.refId = id;
    chip.className = 'chat-ref-chip';
    chip.textContent = label;
    const sel = window.getSelection();
    const canInsert = Boolean(sel && sel.rangeCount && root.contains(sel.anchorNode));
    if (canInsert && sel) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const after = document.createTextNode('\u00a0');
      range.insertNode(after);
      range.insertNode(chip);
      chip.parentNode?.insertBefore(document.createTextNode('\u00a0'), chip);
      sel.removeAllRanges();
      const next = document.createRange();
      next.setStartAfter(after);
      next.collapse(true);
      sel.addRange(next);
    } else {
      if (root.childNodes.length) root.appendChild(document.createTextNode('\u00a0'));
      root.appendChild(chip);
      root.appendChild(document.createTextNode('\u00a0'));
    }
    syncComposerPrompt();
    root.focus();
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 24), 140)}px`;
  }, [prompt]);

  useEffect(() => {
    if (!pendingImageAsk || sending || pendingImageAsk.askResolved) return;
    if (openedImageAskId.current === pendingImageAsk.id) return;
    openedImageAskId.current = pendingImageAsk.id;
    setImageModalOpen(true);
  }, [pendingImageAsk, sending]);

  useEffect(() => {
    if (!pendingVoiceAsk || sending || pendingVoiceAsk.askResolved) return;
    if (openedVoiceAskId.current === pendingVoiceAsk.id) return;
    openedVoiceAskId.current = pendingVoiceAsk.id;
    setVoiceModalOpen(true);
  }, [pendingVoiceAsk, sending]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.id) return;
      const orgId = await getUserOrgId();
      if (!orgId || cancelled) return;
      const threads = await listEditorChatThreads(user.id, orgId);
      if (cancelled) return;
      setHistoryEntries(threads);
    };
    void load();
    return () => { cancelled = true; };
  }, [user?.id, getUserOrgId]);

  useEffect(() => {
    if (!user?.id) return;
    const hasUser = messages.some((m) => m.role === 'user');
    if (!hasUser) return;
    if (persistTimer.current) window.clearTimeout(persistTimer.current);
    persistTimer.current = window.setTimeout(() => {
      void (async () => {
        if (restoringRef.current) return;
        const orgId = await getUserOrgId();
        if (!orgId) return;
        const persisted = toPersistedMessages(messages);
        const title = persisted.find((m) => m.role === 'user')?.text?.slice(0, 80) || 'Novo chat';
        const fromEditor = toPersistedTimeline(timelineContext?.clips);
        const fromAudio = toPersistedAudio(timelineContext?.audioTracks);
        const fromCaptions = toPersistedCaptions(timelineContext?.captions);
        if (fromEditor.length || fromAudio.length || fromCaptions.length) {
          hadTimelineClips.current = true;
          timelinePersistReady.current = true;
        } else if (hadTimelineClips.current) {
          timelinePersistReady.current = true;
        }
        const saved = await upsertEditorChatThread(user.id, orgId, {
          id: historyActiveIdRef.current || undefined,
          title,
          messages: persisted,
          ...(timelinePersistReady.current ? {
            timelineClips: fromEditor,
            timelineAudio: fromAudio,
            timelineCaptions: fromCaptions,
          } : {}),
        });
        if (!saved) return;
        historyActiveIdRef.current = saved.id;
        setHistoryActiveId(saved.id);
        const threads = await listEditorChatThreads(user.id, orgId);
        setHistoryEntries(threads);
      })();
    }, 400);
    return () => {
      if (persistTimer.current) window.clearTimeout(persistTimer.current);
    };
  }, [messages, user?.id, getUserOrgId, timelineContext?.clips, timelineContext?.audioTracks, timelineContext?.captions]);

  useEffect(() => {
    onHistoryChange?.(historyEntries, historyActiveId);
  }, [historyEntries, historyActiveId, onHistoryChange]);

  const pushMsg = (msg: Omit<ChatMsg, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setMessages((prev) => [...prev, { ...msg, id }]);
    return id;
  };
  const patchMsg = (id: string, patch: Partial<ChatMsg>) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const addImageFromUrl = (
    url: string,
    label: string,
    preview?: string,
    kind: 'image' | 'video' = 'image',
    role: 'image' | 'clip' = 'image',
    segmentId?: string,
    generationContext?: string,
    purpose?: EditorImagePurpose,
  ) => {
    setImages((prev) => {
      if (prev.length >= maxRefs) return prev;
      if (segmentId && prev.some((img) => img.segmentId === segmentId)) return prev;
      return [...prev, {
        id: `${Date.now()}-${prev.length}`,
        url,
        preview: preview || url,
        label,
        kind,
        role,
        segmentId,
        generationContext,
        purpose,
      }];
    });
  };

  const addDroppedClip = (raw: {
    id?: string;
    label?: string;
    type?: string;
    sourceUrl?: string;
    blobUrl?: string;
    thumbnailUrl?: string;
    generationContext?: string;
  }) => {
    const kind = raw.type === 'image' ? 'image' : 'video';
    const url = raw.sourceUrl || raw.blobUrl || '';
    if (!url && !raw.id) return;
    addImageFromUrl(
      url || `clip:${raw.id}`,
      raw.label || 'Clip',
      raw.thumbnailUrl || raw.blobUrl || url,
      kind,
      'clip',
      raw.id,
      raw.generationContext,
    );
  };

  const handleComposerDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDropOver(false);
    const refRaw = e.dataTransfer.getData(CHAT_REF_MIME);
    if (refRaw) {
      try {
        const ref = JSON.parse(refRaw) as { id?: string; label?: string };
        if (ref.label) insertRefChip(ref.label, ref.id);
      } catch { /* ignore */ }
      return;
    }
    const clipJson = e.dataTransfer.getData('application/x-seu-clip');
    if (clipJson) {
      try {
        addDroppedClip(JSON.parse(clipJson));
        return;
      } catch { /* ignore */ }
    }
    if (e.dataTransfer.files?.length) {
      void handleImageFiles(e.dataTransfer.files);
    }
  };

  const handleComposerPaste = (e: React.ClipboardEvent) => {
    if (sending) return;
    const pasted = filesFromClipboard(e.clipboardData);
    if (!pasted.length) return;
    // Avoid dumping the bitmap into the contentEditable; attach like file upload.
    e.preventDefault();
    void handleImageFiles(pasted);
  };

  const continueAfterMediaAttach = (extraImages: ChatImage[], extraAudio?: ChatAudio | null) => {
    const pending = messages.find((m) => m.ask && !m.askResolved && m.ask.fields.some(isMediaAskField));
    if (!pending) return;
    const plan = pending.plan || messages.find((m) => m.planConfirmed && m.plan)?.plan;
    const talking = planLooksLikeTalking(plan, pending.text);
    const avatarPlan = planLooksLikeAvatar(plan, pending.text)
      || extraImages.some((img) => img.purpose === 'avatar');
    const avatarCount = extraImages.filter((img) => img.purpose === 'avatar').length;
    const photoCount = extraImages.filter((img) => img.purpose === 'photo').length;
    const wantsAvatar = pending.ask!.fields.some((f) => f.id === 'need_person_image') && avatarPlan;
    const wantsAudio = pending.ask!.fields.some((f) => f.id === 'need_avatar_audio');
    if (wantsAudio && !extraAudio) return;
    if (wantsAvatar && !avatarCount && !extraAudio) return;
    patchMsg(pending.id, { askResolved: true });
    void runAgent(
      extraAudio
        ? 'Áudio anexado. Se ainda faltar a foto da pessoa marcada como Avatar, peça. Senão peça o modelo de vídeo (Avatar). Não gere ainda.'
        : avatarCount
          ? 'Retrato marcado como Avatar anexado. Se ainda faltar áudio, peça. Senão peça o modelo de vídeo. Não gere ainda.'
          : talking && !avatarPlan
            ? 'Foto de cena anexada. Locutor genérico COM fundo: peça Seedance I2V, use esta foto como <<<image_1>>>, generate_audio true, fala no prompt. NÃO peça Kling Avatar nem generate_voice. Não gere ainda.'
            : photoCount
              ? 'Foto de referência anexada. Peça o modelo de vídeo (Seedance). Só peça Avatar se o plano for lip-sync de um retrato. Não gere ainda.'
              : 'Referência anexada. Peça o modelo de vídeo. Não gere ainda.',
      {
        ...(pending.askAnswers || {}),
        ...(plan ? { plan_confirmed: 'true', plan_json: JSON.stringify(plan) } : {}),
      },
      extraImages,
      extraAudio !== undefined ? extraAudio : audio,
    );
  };

  const queueImagePurpose = (list: ChatImage[], thenSend = false) => {
    const need = list.filter(needsImagePurpose);
    if (!need.length) {
      if (thenSend) void runAgent(prompt.trim() || 'Seguir com as referências anexadas.');
      else continueAfterMediaAttach(list);
      return;
    }
    pendingSendAfterPurpose.current = thenSend;
    setPurposeQueue(need);
  };

  const assignImagePurpose = (id: string, purpose: EditorImagePurpose) => {
    setImages((prev) => {
      const marked = prev.find((img) => img.id === id);
      if (marked?.assetId && !marked.assetId.startsWith('local-')) {
        void setMediaImagePurpose(marked.assetId, purpose, marked.generationContext).catch(() => undefined);
      }
      const next = prev.map((img) => (img.id === id ? { ...img, purpose } : img));
      setPurposeQueue((queue) => {
        const wasQueued = queue.some((img) => img.id === id);
        const remaining = queue.filter((img) => img.id !== id);
        if (wasQueued && !remaining.length) {
          window.setTimeout(() => {
            if (pendingSendAfterPurpose.current) {
              pendingSendAfterPurpose.current = false;
              void runAgent(prompt.trim() || 'Seguir com as referências anexadas.', undefined, next);
            } else {
              continueAfterMediaAttach(next);
            }
          }, 0);
        }
        return remaining;
      });
      return next;
    });
  };

  const beginGalleryAttach = (asset: MediaAsset) => {
    const kind = asset.asset_type === 'video' ? 'video' : 'image';
    const url = toMediaUrl(asset.video_url);
    const pendingId = `pending-${asset.id}`;
    setImages((prev) => {
      if (prev.length >= maxRefs) return prev;
      if (prev.some((img) => img.assetId === asset.id || img.id === pendingId)) return prev;
      return [...prev, {
        id: pendingId,
        url,
        preview: kind === 'image' ? (galleryThumbSrc(asset) || url) : url,
        label: asset.title || 'Referência',
        kind,
        role: kind === 'video' ? 'clip' : 'image',
        generationContext: asset.generation_context || undefined,
        purpose: imagePurposeFromAsset(asset),
        assetId: asset.id,
      }];
    });
  };

  const handleGallery = async (result: GalleryPickResult) => {
    if (!result.asset.video_url) return;
    const url = toMediaUrl(result.asset.video_url);
    const kind = result.asset.asset_type === 'video' ? 'video' : 'image';
    if (kind === 'image') rememberUrls([url]);
    const extra: ChatImage = {
      id: `pending-${result.asset.id}`,
      url,
      preview: galleryThumbSrc(result.asset) || url,
      label: result.asset.title || 'Referência',
      kind,
      role: kind === 'video' ? 'clip' : 'image',
      generationContext: result.asset.generation_context || undefined,
      purpose: imagePurposeFromAsset(result.asset),
      assetId: result.asset.id,
    };
    setImages((prev) => {
      const without = prev.filter((img) => img.id !== extra.id && img.assetId !== result.asset.id);
      if (without.length >= maxRefs) return without;
      return [...without, extra];
    });
    const next = [...images.filter((img) => img.id !== extra.id && img.assetId !== result.asset.id), extra];
    if (needsImagePurpose(extra)) queueImagePurpose(next);
    else continueAfterMediaAttach(next);
  };

  const handleImageFiles = async (files: ArrayLike<File> | null) => {
    const picked = files ? Array.from(files) : [];
    if (!picked.length) return;
    const added: ChatImage[] = [];
    for (const raw of picked) {
      const kind = guessLocalMediaKind(raw);
      if (!kind) {
        pushMsg({ role: 'assistant', text: `${raw.name}: envie uma imagem ou um vídeo.` });
        continue;
      }
      if (images.length + added.length >= maxRefs) break;
      const file = fileWithMime(raw, kind);
      const preview = kind === 'image' ? await makeBrowserPreviewUrl(file) : URL.createObjectURL(file);
      added.push({
        id: `${Date.now()}-local-${added.length}`,
        url: preview,
        preview,
        label: file.name.replace(/\.[^.]+$/, '') || (kind === 'video' ? 'Vídeo' : 'Foto'),
        kind,
        role: kind === 'video' ? 'clip' : 'image',
        file,
      });
    }
    if (!added.length) return;
    setImages((prev) => {
      const room = maxRefs - prev.length;
      return room <= 0 ? prev : [...prev, ...added.slice(0, room)];
    });
    const next = [...images, ...added];
    if (next.some(needsImagePurpose)) queueImagePurpose(next);
    else continueAfterMediaAttach(next);
  };

  const attachFrame = async (dataUrl: string, label?: string) => {
    if (!dataUrl.startsWith('data:image/')) return;
    const name = label || 'Frame';
    const preview = dataUrl;
    try {
      const file = dataUrlToFile(dataUrl, `${name.replace(/[^\w.-]+/g, '-')}.jpg`);
      const orgId = await getUserOrgId();
      if (orgId && isCdnUploadEnabled()) {
        const uploaded = await uploadViaCdn({
          organizationId: orgId,
          file,
          contentType: 'image/jpeg',
          filename: file.name,
          purpose: 'frame',
        });
        rememberUrls([uploaded.publicUrl]);
        addImageFromUrl(uploaded.publicUrl, name, preview, 'image', 'image', undefined, undefined, 'photo');
        return;
      }
      addImageFromUrl(preview, name, preview, 'image', 'image', undefined, undefined, 'photo');
    } catch (err) {
      addImageFromUrl(preview, name, preview, 'image', 'image', undefined, undefined, 'photo');
      pushMsg({ role: 'assistant', text: `Frame anexado localmente: ${(err as Error).message}` });
    }
  };

  const handleAudioFile = async (file?: File) => {
    if (!file) return;
    const orgId = await getUserOrgId();
    if (!orgId) return;
    const blob = new Blob([await file.arrayBuffer()], { type: file.type || 'audio/mpeg' });
    const dur = await getAudioDuration(blob);
    if (dur < 2 || dur > 60) {
      pushMsg({ role: 'assistant', text: `O áudio precisa ter entre 2 e 60s (atual: ${dur.toFixed(1)}s).` });
      return;
    }
    try {
      const uploaded = await uploadViaCdn({
        organizationId: orgId,
        file: blob,
        contentType: file.type || 'audio/mpeg',
        filename: file.name || `avatar-${Date.now()}.mp3`,
        purpose: 'audio',
      });
      const nextAudio = { url: uploaded.publicUrl, name: file.name, duration: dur };
      setAudio(nextAudio);
      continueAfterMediaAttach(images, nextAudio);
    } catch (err) {
      pushMsg({ role: 'assistant', text: `Falha no áudio: ${(err as Error).message}` });
    }
  };

  const applyAgentResult = async (statusId: string, data: EditorAgentResponse, orgId: string) => {
    const askFields = (data.ask?.fields || []).filter((f) => f?.id && Array.isArray(f.options));
    const defaults: Record<string, string> = { ...(data.ask?.defaults || {}) };
    for (const field of askFields) {
      if (defaults[field.id]) continue;
      defaults[field.id] = field.options[0]?.value || '';
    }
    const generationCredits = (data.jobs || []).reduce(
      (sum, job) => sum + (Number(job.credits_charged) || 0),
      0,
    ) || data.generation_credits_charged || 0;
    const videoJob = (data.jobs || []).find((job) => job.kind === 'video');
    const imageJob = (data.jobs || []).find((job) => job.kind === 'image');
    const audioJob = (data.jobs || []).find((job) => job.kind === 'audio');
    patchMsg(statusId, {
      text: data.text || 'Pronto.',
      status: undefined,
      credits: data.credits_charged,
      inputTokens: data.input_tokens,
      outputTokens: data.output_tokens,
      generationCredits: generationCredits || undefined,
      generationKind: videoJob ? 'video' : imageJob ? 'image' : audioJob ? 'audio' : undefined,
      modelLabel: videoJob ? 'Vídeo' : imageJob ? 'Imagem' : audioJob ? 'Voz' : undefined,
      ask: askFields.length ? { fields: askFields } : undefined,
      askAnswers: askFields.length ? defaults : undefined,
      askResolved: false,
      plan: data.plan ? normalizeAgentPlan(data.plan) : undefined,
      planConfirmed: false,
    });

    for (const intent of data.intents || []) {
      if (intent.type === 'split_at' && typeof intent.at_seconds === 'number') {
        await onSplitAt?.(intent.at_seconds);
      }
      if (intent.type === 'add_caption' && intent.text) {
        onAddCaption?.({
          text: intent.text,
          startSeconds: intent.start_seconds,
          endSeconds: intent.end_seconds,
          position: intent.position,
          isImpact: intent.is_impact,
        });
      }
      if (intent.type === 'generate_captions') {
        await onGenerateCaptions?.();
      }
      if (intent.type === 'add_audio_track' && intent.url) {
        await onAddAudioTrack?.({
          url: intent.url,
          name: intent.name,
          startOffset: typeof intent.start_offset === 'number'
            ? intent.start_offset
            : timelineContext?.playhead,
          kind: intent.kind || 'import',
          loop: intent.loop,
        });
      }
      if (intent.type === 'update_timeline_audio') {
        onUpdateTimelineAudio?.({
          voice_volume: intent.voice_volume,
          clip_volume: intent.clip_volume,
          music_volume: intent.music_volume,
          sfx_volume: intent.sfx_volume,
          music_loop: intent.music_loop,
          music_speed: intent.music_speed,
          mute_music: intent.mute_music,
          mute_sfx: intent.mute_sfx,
          mute_voice: intent.mute_voice,
          mute_clips: intent.mute_clips,
        });
      }
      if (intent.type === 'set_clip_context' && intent.clip_id && intent.context) {
        onSetClipContext?.(intent.clip_id, intent.context);
      }
    }

    const place = async (
      url: string,
      label: string,
      kind: 'video' | 'image',
      at?: number,
      index?: number,
      extra?: { jobId?: string; mediaAssetId?: string; generationContext?: string },
    ) => {
      if (onInsertGenerated) {
        await onInsertGenerated({
          url,
          label,
          kind,
          atSeconds: at,
          index,
          jobId: extra?.jobId,
          mediaAssetId: extra?.mediaAssetId,
          generationContext: extra?.generationContext,
        });
      } else if (kind === 'video') {
        await onVideoGenerated(url, label);
      }
      setMessages((prev) => prev.map((m) => (
        m.id !== statusId
          ? m
          : {
            ...m,
            images: [...(m.images || []), {
              id: `${Date.now()}-${kind}`,
              url,
              preview: url,
              label,
              kind,
              role: kind === 'video' ? 'clip' : 'image',
            }],
          }
      )));
    };

    for (const intent of data.intents || []) {
      if (intent.type !== 'insert_clip') continue;
      if (intent.job_id) continue;
      const kind = intent.kind === 'image' ? 'image' : 'video';
      let url = intent.url ? toMediaUrl(intent.url) : '';
      let mediaAssetId = intent.media_asset_id;
      let generationContext: string | undefined;
      if (intent.media_asset_id) {
        const assets = await fetchOrgMediaAssets(orgId, { status: galleryListStatus(role) });
        const hit = assets.find((a) => a.id === intent.media_asset_id);
        if (hit?.video_url) url = url || toMediaUrl(hit.video_url);
        generationContext = hit?.generation_context || undefined;
        mediaAssetId = hit?.id || intent.media_asset_id;
      }
      if (!url) continue;
      await place(url, intent.label || 'Mídia', kind, intent.at_seconds ?? timelineContext?.playhead, intent.index, {
        mediaAssetId,
        generationContext,
      });
    }

    for (const job of data.jobs || []) {
      patchMsg(statusId, { status: `Gerando ${job.kind === 'video' ? 'vídeo' : job.kind === 'audio' ? audioJobLabel(job.audio_kind).toLowerCase() : 'imagem'}…` });
      const url = await waitForJobResultUrl(job.job_id, {
        timeoutMs: 20 * 60 * 1000,
        onStatus: (j) => patchMsg(statusId, { status: jobStatusLabel(j) }),
      });
      const publicUrl = toMediaUrl(url);
      if (job.kind === 'audio') {
        try {
          const placed = await onAddAudioTrack?.({
            url: publicUrl,
            name: job.label || audioJobLabel(job.audio_kind),
            startOffset: typeof job.at_seconds === 'number'
              ? job.at_seconds
              : timelineContext?.playhead,
            kind: isSoundJobKind(job.audio_kind) ? (job.audio_kind === 'music' ? 'music' : 'sfx') : 'voice',
            loop: Boolean(job.loop),
            generationContext: job.label,
          });
          const measured = typeof placed === 'number' && placed > 0
            ? placed
            : await measureAudioDuration(publicUrl);
          patchMsg(statusId, {
            audio: { url: publicUrl, name: job.label || audioJobLabel(job.audio_kind), duration: measured },
            generationKind: 'audio',
            modelLabel: audioJobLabel(job.audio_kind),
          });
        } catch (err) {
          patchMsg(statusId, {
            text: `Áudio pronto, mas falhou ao colocar na timeline: ${(err as Error).message}`,
            status: undefined,
          });
        }
        continue;
      }
      if (job.kind === 'image') rememberUrls([publicUrl]);
      await place(publicUrl, job.label || 'Gerado', job.kind, job.at_seconds ?? timelineContext?.playhead, job.index, {
        jobId: job.job_id,
        generationContext: job.generation_context,
      });
    }

    if ((data.jobs && data.jobs.length) || (data.intents || []).some((i) => i.type === 'insert_clip' || i.type === 'split_at')) {
      patchMsg(statusId, { status: undefined, text: data.text || 'Pronto. A timeline foi atualizada.' });
    }
  };

  const runAgent = async (text: string, answers?: Record<string, string>, extraImages?: ChatImage[], extraAudio?: ChatAudio | null) => {
    setSending(true);
    // Modal/plan turns send no media; recycle thread photos only then.
    // Free-typed messages must NOT reattach the first WhatsApp photo from earlier.
    const turnImages = extraImages ?? images.map((img) => ({ ...img }));
    const recycleThreadMedia = Boolean(answers);
    const talkingPlan = planLooksLikeTalking(
      parsePlanJson(answers?.plan_json)
        || [...messages].reverse().find((m) => m.planConfirmed && m.plan)?.plan
        || [...messages].reverse().find((m) => m.plan)?.plan,
      text,
    );
    const constructionTurn = /constru|obra|evolu|andar por andar|drone|fachada|canteiro|time.?lapse/i.test(text);
    const planForStrip = parsePlanJson(answers?.plan_json)
      || [...messages].reverse().find((m) => m.planConfirmed && m.plan)?.plan
      || [...messages].reverse().find((m) => m.plan)?.plan;
    const wantsBackdrop = wantsSceneBackdrop(`${text} ${planBlob(planForStrip)}`);
    const stripScenePhotos = talkingPlan && !constructionTurn && !wantsBackdrop;
    const scopedTurnImages = stripScenePhotos
      ? turnImages.filter((img) => img.purpose === 'avatar' || img.role === 'clip' || img.kind === 'video')
      : turnImages;
    const sentImages = scopedTurnImages.length
      ? scopedTurnImages
      : (recycleThreadMedia && !stripScenePhotos ? threadImages.current.map((img) => ({ ...img })) : []);
    const turnAudio = extraAudio !== undefined ? extraAudio : audio;
    const sentAudio = turnAudio || (recycleThreadMedia ? threadAudio.current : null);
    const orgIdForClips = await getUserOrgId();
    const resolvedImages = await Promise.all(sentImages.map(async (img) => {
      if (isHttpUrl(img.url)) return img;
      if (img.file || ((img.url || img.preview) && !img.segmentId)) {
        if (!orgIdForClips) return img;
        return promoteLocalChatImage(img, orgIdForClips);
      }
      if (!img.segmentId || !getSegment || !orgIdForClips) return img;
      const seg = getSegment(img.segmentId);
      if (!seg) return img;
      if (isHttpUrl(seg.sourceUrl)) {
        return { ...img, url: seg.sourceUrl, generationContext: img.generationContext || seg.generationContext };
      }
      try {
        const blob = await prepareClipBlob(seg);
        const url = await uploadBaseVideo(orgIdForClips, blob);
        return { ...img, url };
      } catch {
        return img;
      }
    }));
    const refUrls = stripScenePhotos
      ? resolvedImages.filter((img) => img.purpose === 'avatar' && isHttpUrl(img.url)).map((img) => img.url)
      : collectRefUrls(resolvedImages.filter((img) => img.role !== 'clip'), { includeHistory: recycleThreadMedia });
    const clipUrls = resolvedImages
      .filter((img) => img.role === 'clip' && isHttpUrl(img.url))
      .map((img) => img.url);
    rememberUrls(refUrls);
    const keepForThread = resolvedImages.filter((img) => img.role !== 'clip' && isHttpUrl(img.url));
    if (keepForThread.length) threadImages.current = keepForThread;
    if (sentAudio) threadAudio.current = sentAudio;
    setSending(true);
    if (!answers) {
      clearComposer();
      setImages([]);
      setAudio(null);
    }

    const history: EditorAgentHistoryItem[] = messages
      .filter((m) => m.text)
      .slice(-12)
      .map((m) => ({ role: m.role, text: m.text }));

    if (!answers) {
      pushMsg({
        role: 'user',
        text,
        images: turnImages.length ? resolvedImages : undefined,
        audio: turnAudio || undefined,
      });
    }
    const statusId = pushMsg({
      role: 'assistant',
      text: answers ? 'Continuando…' : 'Pensando…',
      status: 'Agente…',
    });

    try {
      const orgId = await getUserOrgId();
      if (!orgId) throw new Error('Organização não encontrada.');

      const injectLastGen = recycleThreadMedia
        || wantsBackdrop
        || /use (essa|esta|a) imagem|imagem gerada|ao fundo|de fundo/i.test(text);
      const referenceImageUrls = stripScenePhotos
        ? refUrls
        : (injectLastGen && lastGeneratedImageUrl
          ? [lastGeneratedImageUrl, ...refUrls.filter((url) => url !== lastGeneratedImageUrl)]
          : refUrls);

      const response = await supabase.functions.invoke('editor-agent', {
        body: {
          organization_id: orgId,
          message: text,
          history,
          answers: answers || undefined,
          timeline: timelineContext || { clips: [], totalDuration: 0, playhead: 0 },
          defaults: {
            aspect_ratio: canvasAspect,
            reference_image_urls: referenceImageUrls,
            audio_url: sentAudio?.url,
            avatar_image_url: resolvedImages.find((img) => img.purpose === 'avatar' && isHttpUrl(img.url))?.url,
            selected_clip_label: selectedSegment?.label,
            selected_image_url: stripScenePhotos ? undefined : selectedImage?.sourceUrl,
            selected_clip_context: selectedSegment?.generationContext,
            last_generated_image_url: stripScenePhotos || !injectLastGen
              ? undefined
              : (lastGeneratedImageUrl || undefined),
            reference_video_urls: clipUrls,
            plan_ready: [...messages].reverse().some((m) => m.planConfirmed && Boolean(m.plan?.scenes?.length)),
          },
          attachments: resolvedImages.map((img) => ({
            url: img.url,
            label: img.label,
            kind: img.kind || 'image',
            clip_id: img.segmentId,
            generation_context: img.generationContext,
            purpose: img.purpose,
          })),
          see_images: sentImages.length > 0,
        },
      });
      const data = response.data as EditorAgentResponse & { error?: string };
      if (data?.error) throw new Error(data.error);
      if (response.error) throw new Error(response.error.message);
      setSending(false);
      await applyAgentResult(statusId, data, orgId);
    } catch (err) {
      patchMsg(statusId, {
        text: (err as Error).message || 'Falha no agente.',
        status: undefined,
      });
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    const text = serializeComposer(textareaRef.current).trim() || prompt.trim() || (images[0]?.label ? `Usar ${images[0].label}` : '');
    if ((!text && !images.length && !audio) || sending) return;
    if (images.some(needsImagePurpose)) {
      queueImagePurpose(images, true);
      return;
    }
    await runAgent(text || 'Seguir com as referências anexadas.');
  };

  const handleSendWithPlanConfirm = async () => {
    const text = serializeComposer(textareaRef.current).trim() || prompt.trim();
    const pendingPlan = [...messages].reverse().find((m) => m.plan && !m.planConfirmed);
    if (pendingPlan && text && PLAN_CONFIRM_TEXT.test(text)) {
      handlePlanConfirm(pendingPlan);
      return;
    }
    await handleSend();
  };

  // "tudo certo" digitado vale pelo clique no Confirmo! — sem isso o plano fica
  // pendente para sempre e o agente só narra que vai gerar.
  const PLAN_CONFIRM_TEXT = /^(tudo certo|confirmo|confirmado|pode gerar|pode seguir|pode ir|pode fazer|ok|perfeito|isso|sim|bora|vamos|aprovo|aprovado|est[aá] certo|certo|gere|gera|gerar|continue|continuar|prossiga|proseguir|pode continuar|pode prosseguir)[\s!.…]*$/i;

  const handlePlanConfirm = (msg: ChatMsg) => {
    if (!msg.plan || msg.planConfirmed || sending) return;
    const plan = normalizeAgentPlan(msg.plan);
    patchMsg(msg.id, {
      planConfirmed: true,
      askResolved: true,
      plan,
    });
    const talking = planLooksLikeTalking(plan);
    const avatarPlan = planLooksLikeAvatar(plan) || images.some((img) => img.purpose === 'avatar');
    const wantsBackdrop = wantsSceneBackdrop(planBlob(plan));
    void runAgent(
      avatarPlan
        ? 'Plano confirmado. Lip-sync de um rosto real: peça um retrato marcado como Avatar e o áudio da fala. Não gere ainda.'
        : talking && wantsBackdrop
          ? 'Plano confirmado. Locutor genérico COM fundo da obra: peça Seedance I2V, passe a imagem da fachada/obra em reference_image_urls (<<<image_1>>>), generate_audio true, fala no prompt. NÃO chame generate_voice nem Kling Avatar. Não gere ainda.'
          : talking
            ? 'Plano confirmado. Locutor genérico: peça Seedance T2V com áudio nativo. A fala vai no prompt. NÃO peça retrato, voz TTS nem Kling Avatar. Não gere ainda.'
            : 'Plano confirmado. Se a cena for só imóvel/obra e já houver foto de referência, peça o modelo de vídeo. Só peça retrato se for lip-sync de um rosto real. Não gere ainda.',
      { plan_confirmed: 'true', plan_json: JSON.stringify(plan) },
      talking && !wantsBackdrop
        ? images.filter((img) => img.purpose === 'avatar' || img.role === 'clip')
        : images,
      audio,
    );
  };

  const handleAskConfirm = (msg: ChatMsg) => {
    if (msg.askResolved || sending) return;
    if (msg.ask?.fields.some((f) => f.id === 'image_model_id')) {
      setImageModalOpen(true);
      return;
    }
    if (msg.ask?.fields.some((f) => isVoiceAskField(f) || isSfxAskField(f) || isMusicAskField(f))) {
      setVoiceModalOpen(true);
      return;
    }
    if (msg.ask?.fields.some((f) => f.id === 'need_person_image' || f.id === 'need_reference_image')) {
      setGalleryOpen(true);
      return;
    }
    if (msg.ask?.fields.some((f) => f.id === 'need_avatar_audio')) {
      audioInputRef.current?.click();
      return;
    }
    if (msg.ask?.fields.some((f) => f.id === 'video_model_id')) {
      setModelModalOpen(true);
      return;
    }
    if (!msg.ask) return;
    const answers: Record<string, string> = {};
    for (const field of msg.ask.fields) {
      answers[field.id] = msg.askAnswers?.[field.id] || field.options[0]?.value || '';
    }
    patchMsg(msg.id, { askResolved: true, askAnswers: answers });
    void runAgent('Seguir com as opções escolhidas.', answers, [], null);
  };

  const startNewChat = () => {
    setHistoryActiveId(null);
    historyActiveIdRef.current = null;
    setMessages([]);
    clearComposer();
    setImages([]);
    setPurposeQueue([]);
    pendingSendAfterPurpose.current = false;
    setAudio(null);
    threadImages.current = [];
    threadAudio.current = null;
    setModelModalOpen(false);
    setImageModalOpen(false);
    setVoiceModalOpen(false);
    setMediaPreview(null);
    openedImageAskId.current = null;
    openedVoiceAskId.current = null;
    hadTimelineClips.current = false;
    timelinePersistReady.current = false;
    restoringRef.current = true;
    Promise.resolve(onRestoreTimelineClips?.([], [], []))
      .finally(() => { restoringRef.current = false; });
  };

  const restoreChat = (thread: EditorChatThread) => {
    setHistoryActiveId(thread.id);
    historyActiveIdRef.current = thread.id;
    setMessages(thread.messages.map((m) => ({ ...m })));
    clearComposer();
    setImages([]);
    setPurposeQueue([]);
    pendingSendAfterPurpose.current = false;
    setAudio(null);
    const lastSent = [...thread.messages].reverse().find((m) => m.role === 'user' && m.images?.length);
    threadImages.current = (lastSent?.images || []).filter((img) => img.role !== 'clip' && isHttpUrl(img.url));
    threadAudio.current = [...thread.messages].reverse().find((m) => m.role === 'user' && m.audio)?.audio || null;
    setModelModalOpen(false);
    setImageModalOpen(false);
    setVoiceModalOpen(false);
    setMediaPreview(null);
    openedImageAskId.current = null;
    openedVoiceAskId.current = null;
    hadTimelineClips.current = Boolean(thread.timelineClips?.length || thread.timelineAudio?.length || thread.timelineCaptions?.length);
    timelinePersistReady.current = false;
    restoringRef.current = true;
    const clips = timelineClipsFromThread(thread);
    Promise.resolve(onRestoreTimelineClips?.(clips, timelineAudioFromThread(thread), thread.timelineCaptions || []))
      .finally(() => { restoringRef.current = false; });
  };

  useImperativeHandle(ref, () => ({ startNewChat, restoreChat, attachFrame }));

  const handleVideoModelConfirm = (value: VideoModelPickerValue) => {
    if (!pendingVideoAsk || pendingVideoAsk.askResolved || sending) return;
    const answers: Record<string, string> = { ...(pendingVideoAsk.askAnswers || {}) };
    for (const field of pendingVideoAsk.ask?.fields || []) {
      if (VIDEO_ASK_IDS.has(field.id)) continue;
      answers[field.id] = pendingVideoAsk.askAnswers?.[field.id] || field.options[0]?.value || '';
    }
    answers.video_model_id = value.videoModelId;
    answers.resolution_id = value.resolutionId;
    answers.duration_seconds = String(value.durationSeconds);
    answers.generate_audio = value.generateAudio === false ? 'false' : 'true';
    answers.plan_confirmed = 'true';
    const confirmedPlan = pendingVideoAsk.plan || messages.find((m) => m.planConfirmed && m.plan)?.plan;
    if (confirmedPlan) answers.plan_json = JSON.stringify(confirmedPlan);
    patchMsg(pendingVideoAsk.id, { askResolved: true, askAnswers: answers });
    setModelModalOpen(false);
    void runAgent('Seguir com o modelo escolhido no modal.', answers, [], null);
  };

  const handleVoiceModelConfirm = (value: EditorVoiceValue) => {
    if (!pendingVoiceAsk || pendingVoiceAsk.askResolved || sending) return;
    const answers: Record<string, string> = { ...(pendingVoiceAsk.askAnswers || {}) };
    for (const field of pendingVoiceAsk.ask?.fields || []) {
      if (VOICE_ASK_IDS.has(field.id) || SFX_ASK_IDS.has(field.id) || MUSIC_ASK_IDS.has(field.id)) continue;
      answers[field.id] = pendingVoiceAsk.askAnswers?.[field.id] || field.options[0]?.value || '';
    }
    answers.voice_model_id = value.voiceModelId;
    answers.sfx_model_id = value.kind === 'sfx' || value.kind === 'music' ? value.voiceModelId : (answers.sfx_model_id || '');
    answers.music_model_id = value.kind === 'music' ? value.voiceModelId : (answers.music_model_id || '');
    answers.voice_id = value.voiceId;
    answers.voice_text = value.text;
    answers.audio_kind = value.kind;
    answers.sound_loop = value.soundLoop ? 'true' : 'false';
    if (value.sourceAudioUrl) answers.upload_url = value.sourceAudioUrl;
    patchMsg(pendingVoiceAsk.id, { askResolved: true, askAnswers: answers });
    setVoiceModalOpen(false);
    const followUp = value.kind === 'voice'
      ? `Gerar locução com o texto: "${value.text}"`
      : (value.text || 'Seguir com o áudio escolhido no modal.');
    void runAgent(followUp, answers, [], null);
  };

  const handleImageModelConfirm = (imageModelId: string) => {
    if (!pendingImageAsk || pendingImageAsk.askResolved || sending) return;
    const answers: Record<string, string> = { ...(pendingImageAsk.askAnswers || {}) };
    for (const field of pendingImageAsk.ask?.fields || []) {
      if (IMAGE_ASK_IDS.has(field.id)) continue;
      answers[field.id] = pendingImageAsk.askAnswers?.[field.id] || field.options[0]?.value || '';
    }
    answers.image_model_id = imageModelId;
    patchMsg(pendingImageAsk.id, { askResolved: true, askAnswers: answers });
    setImageModalOpen(false);
    void runAgent('Seguir com o modelo de imagem escolhido no modal.', answers, [], null);
  };

  const imageThumbs = images.filter((img) => img.role !== 'clip');
  const clipChips = images.filter((img) => img.role === 'clip');
  const pendingTalkingAsk = planLooksLikeTalking(pendingVideoAsk?.plan, pendingVideoAsk?.text);
  const pendingAvatarAsk = planLooksLikeAvatar(pendingVideoAsk?.plan, pendingVideoAsk?.text)
    || imageThumbs.some((img) => img.purpose === 'avatar');
  const pendingBackdrop = wantsSceneBackdrop(
    `${pendingVideoAsk?.text || ''} ${planBlob(pendingVideoAsk?.plan)}`,
  ) || Boolean(lastGeneratedImageUrl) || imageThumbs.some((img) => img.purpose === 'photo');
  const composerModality = clipChips.length
    ? 'v2v'
    : pendingTalkingAsk && !pendingAvatarAsk && !pendingBackdrop
      ? 't2v'
      : (collectRefUrls(imageThumbs).length || selectedImage || (pendingTalkingAsk && pendingBackdrop && lastGeneratedImageUrl)
        ? 'i2v'
        : 't2v');
  const videoAskRankText = [
    pendingVideoAsk?.text,
    pendingVideoAsk?.plan?.scenes.map((s) => `${s.label} ${s.prompt}`).join(' '),
  ].filter(Boolean).join(' ');
  const videoAskPreferredIds = (pendingVideoAsk?.ask?.fields || [])
    .find((f) => f.id === 'video_model_id')
    ?.options.map((o) => o.value)
    .filter(Boolean) || [];
  const rankedVideoModels = rankVideoModelsForRequest({
    modality: composerModality,
    text: videoAskRankText,
    preferredIds: videoAskPreferredIds,
  });
  const rankedVideoModelIds = rankedVideoModels.map((m) => m.id);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: dock ? undefined : '100%',
      minWidth: 0,
      minHeight: 0,
      gap: 8,
    }}>
      <div
        ref={listRef}
        style={{
          flex: dock ? undefined : 1,
          overflowY: 'auto',
          minHeight: dock ? 88 : 120,
          maxHeight: dock ? 148 : undefined,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: dock ? '2px 2px 0' : 0,
        }}
      >
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            msg={m}
            onAskChange={(fieldId, value) => {
              setMessages((prev) => prev.map((x) => (
                x.id === m.id
                  ? { ...x, askAnswers: { ...(x.askAnswers || {}), [fieldId]: value } }
                  : x
              )));
            }}
            onAskConfirm={
              (m.ask && !m.askResolved)
                ? () => handleAskConfirm(m)
                : undefined
            }
            onPlanConfirm={m.plan && !m.planConfirmed && !sending ? () => handlePlanConfirm(m) : undefined}
            onOpenMedia={(img) => setMediaPreview(img)}
            onAttachGallery={m.ask && !m.askResolved && m.ask.fields.some((f) => f.id === 'need_person_image' || f.id === 'need_reference_image')
              ? () => setGalleryOpen(true)
              : undefined}
            onAttachAudio={m.ask && !m.askResolved && m.ask.fields.some((f) => f.id === 'need_avatar_audio')
              ? () => audioInputRef.current?.click()
              : undefined}
          />
        ))}
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDropOver(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropOver(false);
        }}
        onDrop={handleComposerDrop}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: '10px 10px 8px',
          borderRadius: 16,
          border: dropOver ? '1px solid rgba(129,140,248,0.55)' : '1px solid rgba(255,255,255,0.1)',
          background: dropOver ? 'rgba(99,102,241,0.12)' : 'rgba(0,0,0,0.32)',
          flexShrink: 0,
        }}
      >
        {imageThumbs.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {imageThumbs.map((img) => (
              <ComposerImageThumb
                key={img.id}
                img={img}
                onRemove={() => {
                  setImages((prev) => prev.filter((x) => x.id !== img.id));
                  setPurposeQueue((prev) => prev.filter((x) => x.id !== img.id));
                }}
                onPurpose={(purpose) => assignImagePurpose(img.id, purpose)}
              />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 6 }}>
          {clipChips.map((img) => (
            <RefChip
              key={img.id}
              label={img.label}
              kind={img.kind}
              onRemove={() => setImages((prev) => prev.filter((x) => x.id !== img.id))}
            />
          ))}
          <div
            ref={textareaRef}
            role="textbox"
            aria-multiline="true"
            contentEditable={!sending}
            onInput={syncComposerPrompt}
            onPaste={handleComposerPaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSendWithPlanConfirm();
              }
            }}
            data-placeholder={
              dropOver
                ? 'Solte a imagem para citar no texto…'
                : sending
                  ? 'Agente a pensar…'
                  : clipChips.length
                    ? 'Descreva a edição neste clip…'
                    : 'Peça a cena, um corte, legendas…'
            }
            data-empty={!prompt.trim() ? '1' : '0'}
            suppressContentEditableWarning
            style={{
              flex: '1 1 160px',
              minWidth: 120,
              minHeight: 24,
              maxHeight: 140,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'rgba(255,255,255,0.88)',
              fontSize: '0.8rem',
              fontFamily: 'inherit',
              lineHeight: 1.45,
              padding: '2px 0 0',
              overflowY: 'auto',
              opacity: sending ? 0.55 : 1,
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input ref={imageInputRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime,.heic,.heif,.avif,.bmp,.tif,.tiff,.svg,.jfif" multiple hidden onChange={(e) => {
            const snapshot = Array.from(e.target.files || []);
            e.target.value = '';
            void handleImageFiles(snapshot);
          }} />
          <input ref={audioInputRef} type="file" accept="audio/*" hidden onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            void handleAudioFile(file);
          }} />
          {([
            { title: 'Anexar da galeria', onClick: () => setGalleryOpen(true), disabled: images.length >= maxRefs, icon: <ImagePlus size={15} />, active: imageThumbs.length > 0 },
            { title: 'Upload local', onClick: () => imageInputRef.current?.click(), disabled: images.length >= maxRefs, icon: <Paperclip size={15} />, active: false },
            { title: 'Áudio (avatar ou voz)', onClick: () => audioInputRef.current?.click(), disabled: false, icon: <Mic size={15} />, active: Boolean(audio) },
          ] as const).map((btn) => (
            <button
              key={btn.title}
              type="button"
              title={btn.title}
              onClick={btn.onClick}
              disabled={btn.disabled}
              style={{
                border: 'none',
                background: 'none',
                color: btn.active ? '#a5b4fc' : 'rgba(255,255,255,0.5)',
                cursor: btn.disabled ? 'default' : 'pointer',
                width: 28,
                height: 28,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                padding: 0,
              }}
            >
              {btn.icon}
            </button>
          ))}
          {audio && (
            <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', marginLeft: 4 }}>
              {audio.duration.toFixed(1)}s
            </span>
          )}
          <button
            type="button"
            onClick={() => void handleSendWithPlanConfirm()}
            disabled={sending || (!prompt.trim() && !images.length && !audio)}
            style={{
              marginLeft: 'auto',
              border: 'none',
              borderRadius: 8,
              width: 28,
              height: 28,
              flexShrink: 0,
              background: sending || (!prompt.trim() && !images.length && !audio) ? 'rgba(255,255,255,0.06)' : '#6366f1',
              color: '#fff',
              cursor: sending || (!prompt.trim() && !images.length && !audio) ? 'default' : 'pointer',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Send size={13} />
          </button>
        </div>
      </div>

      <GalleryMediaPicker
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onSelectStart={beginGalleryAttach}
        onSelect={(r) => { void handleGallery(r); }}
        accept="both"
        multiple
        title="Anexar da galeria"
        saveUploadsToGallery={false}
      />
      <EditorImagePurposeModal
        open={purposeQueue.length > 0}
        preview={purposeQueue[0]?.preview || purposeQueue[0]?.url}
        label={purposeQueue[0]?.label}
        remaining={purposeQueue.length}
        onPick={(purpose) => {
          if (purposeQueue[0]) assignImagePurpose(purposeQueue[0].id, purpose);
        }}
        onClose={() => {
          pendingSendAfterPurpose.current = false;
          setPurposeQueue([]);
        }}
      />
      <EditorVideoModelModal
        open={modelModalOpen && Boolean(pendingVideoAsk)}
        prompt={videoModelHint(videoAskRankText, rankedVideoModels[0]?.label)}
        modality={composerModality}
        recommendedModelId={rankedVideoModelIds[0] || null}
        preferredModelIds={rankedVideoModelIds}
        recommendedHint="Sugerido para este pedido"
        onClose={() => setModelModalOpen(false)}
        onConfirm={handleVideoModelConfirm}
      />
      <EditorImageModelModal
        open={imageModalOpen && Boolean(pendingImageAsk)}
        prompt={pendingImageAsk?.text}
        onClose={() => setImageModalOpen(false)}
        onConfirm={handleImageModelConfirm}
      />
      <EditorVoiceModal
        open={voiceModalOpen && Boolean(pendingVoiceAsk)}
        kind={voiceModalKind(pendingVoiceAsk?.ask?.fields, pendingVoiceAsk?.text)}
        prompt={voiceModalSeedText(pendingVoiceAsk)}
        sourceTracks={timelineContext?.audioTracks}
        onClose={() => setVoiceModalOpen(false)}
        onConfirm={handleVoiceModelConfirm}
      />
      {mediaPreview && (
        <ModalOverlay className="generation-preview-overlay" onClose={() => setMediaPreview(null)}>
          <div
            className="generation-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-label={mediaPreview.label || 'Pré-visualização'}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="generation-preview-header">
              <div>
                <h3>{mediaPreview.label || (mediaPreview.kind === 'video' ? 'Vídeo' : 'Imagem')}</h3>
              </div>
              <button
                type="button"
                className="generation-preview-close"
                onClick={() => setMediaPreview(null)}
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>
            <div className="generation-preview-media">
              {mediaPreview.kind === 'video' ? (
                <video src={mediaPreview.url || mediaPreview.preview} controls autoPlay playsInline />
              ) : (
                <img src={mediaPreview.url || mediaPreview.preview} alt={mediaPreview.label || ''} />
              )}
            </div>
          </div>
        </ModalOverlay>
      )}
      <style>{`
        .spin { animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .chat-ref-chip {
          display: inline-flex;
          align-items: center;
          max-width: 160px;
          margin: 0 2px;
          padding: 1px 8px;
          border-radius: 999px;
          background: rgba(99,102,241,0.28);
          border: 1px solid rgba(129,140,248,0.4);
          color: #e0e7ff;
          font-size: 0.68rem;
          font-weight: 650;
          line-height: 1.35;
          vertical-align: middle;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        [contenteditable][data-empty="1"]:before {
          content: attr(data-placeholder);
          color: rgba(255,255,255,0.35);
          pointer-events: none;
        }
      `}</style>
    </div>
  );
});

export default EditorChat;
